import { useEffect, useState } from "react";
import { ShoppingCart, DollarSign, Package, Users } from "lucide-react";
import { api, money } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  payment_received: "bg-emerald-100 text-emerald-800",
  processing: "bg-blue-100 text-blue-800",
  shipped: "bg-indigo-100 text-indigo-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-rose-100 text-rose-800",
};
export const statusLabel = (s) => s.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
export function StatusBadge({ status }) {
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLES[status] || "bg-slate-100 text-slate-700"}`}>{statusLabel(status)}</span>;
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-border bg-white p-6">
      <div className="flex items-center justify-between">
        <span className="overline text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="mt-3 font-head text-3xl font-extrabold tracking-tight">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);
  if (!stats) return <div className="text-muted-foreground">Loading…</div>;
  return (
    <div data-testid="dashboard-page">
      <h1 className="font-head text-3xl font-extrabold tracking-tight">Dashboard</h1>
      <p className="mt-1 text-sm text-muted-foreground">Overview of your ATLABS store</p>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Stat icon={ShoppingCart} label="Total Orders" value={stats.orders_count} />
        <Stat icon={DollarSign} label="Confirmed Revenue" value={money(stats.paid_revenue)} />
        <Stat icon={Package} label="Products" value={stats.products_count} />
        <Stat icon={Users} label="Customers" value={stats.customers_count} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-white p-6 lg:col-span-1">
          <h2 className="font-head text-lg font-bold">Orders by Status</h2>
          <div className="mt-4 space-y-2">
            {Object.entries(stats.by_status).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <StatusBadge status={k} />
                <span className="font-semibold">{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-white p-6 lg:col-span-2">
          <h2 className="font-head text-lg font-bold">Recent Orders</h2>
          <div className="mt-4 divide-y divide-border">
            {stats.recent_orders.length === 0 && <p className="text-sm text-muted-foreground">No orders yet.</p>}
            {stats.recent_orders.map((o) => (
              <div key={o.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="font-semibold">{o.order_number}</div>
                  <div className="text-xs text-muted-foreground">{o.customer?.name || "—"}</div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={o.status} />
                  <span className="font-semibold">{money(o.total)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
